import type { FormInstance } from "antd";
import { useCallback, useRef } from "react";
import {
  collectServiceIds,
  getUsedMaterialIds,
  type MaterialUsage,
  reconcileServiceMaterials,
} from "../../common/utils/serviceMaterials";
import type { Services } from "../generated-axios-client";

// Keeps a ticket form's "materials" field in line with the work order and task
// services. Call snapshotServiceIds after loading values into the form, and
// syncServiceMaterials after a service is set, changed or removed.
export const useServiceMaterialsSync = (
  form: FormInstance,
  serviceItems: Services[] | null,
  storedMaterialUsages: MaterialUsage[]
) => {
  const serviceIdsRef = useRef<string[]>([]);

  const currentServiceIds = useCallback(
    () => collectServiceIds(form.getFieldValue("service"), form.getFieldValue("items")),
    [form]
  );

  const snapshotServiceIds = useCallback(() => {
    serviceIdsRef.current = currentServiceIds();
  }, [currentServiceIds]);

  const syncServiceMaterials = () => {
    const nextServiceIds = currentServiceIds();
    form.setFieldValue(
      "materials",
      reconcileServiceMaterials({
        materialIds: (form.getFieldValue("materials") as string[] | undefined) ?? [],
        previousServiceIds: serviceIdsRef.current,
        nextServiceIds,
        findService: (serviceId) => serviceItems?.find((s) => s.id === serviceId),
        usedMaterialIds: getUsedMaterialIds(storedMaterialUsages),
      })
    );
    serviceIdsRef.current = nextServiceIds;
  };

  return { snapshotServiceIds, syncServiceMaterials };
};
